import { memo } from "react";
import { formatCurrency, transformDateToLocalDateString } from "helpers";
import { FleetVehicle } from "services/api/fleet/vehicles/types";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import Tooltip from "ui/components/Tooltip/Tooltip";

export interface VehicleCellProps {
  vehicle: FleetVehicle | null | undefined;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const VehicleCell: React.FC<VehicleCellProps> = ({ vehicle, onClick }) => {
  if (!vehicle) return <></>;

  const fullName = [
    vehicle.vehicleManufacturer?.name,
    vehicle.vehicleModel?.name,
    vehicle.vehicleType?.name,
  ].filter(Boolean).join(" ");

  // Ikona i plate ostaju izvan Tooltip-a; samo glavni tekst (fullName) ide u
  // `decorate` da `border-b border-dashed` (koji Tooltip dodaje oko decorate)
  // bude samo pod tekstom, a ne pod cijelim retkom.
  return (
    <div className="flex items-center gap-3 cursor-pointer" onClick={onClick}>
      <TableIcon
        imageUrl={vehicle.defaultImage}
        initials={vehicle.vehicleManufacturer?.name?.charAt(0).toUpperCase() ?? "?"}
      />
      <div className="min-w-0">
        <Tooltip
          decorate={
            <span className="text-sm font-medium text-sky-600 hover:text-sky-900 truncate">
              {fullName}
            </span>
          }
          position="bottom-left"
          size="medium"
        >
          <div className="min-w-[280px]">
          {vehicle.defaultImage && (
            <div className="mb-3 rounded overflow-hidden">
              <img src={vehicle.defaultImage} alt={fullName} className="w-full h-[120px] object-cover rounded" />
            </div>
          )}
          <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Detalji vozila</div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-300">Naziv</span>
            <span className="font-medium text-right max-w-[180px] truncate">{fullName}</span>
          </div>
          {vehicle.vinNumber && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">VIN</span>
              <span className="font-medium text-xs">{vehicle.vinNumber}</span>
            </div>
          )}
          {vehicle.registrationPlate && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Registracija</span>
              <span className="font-medium">{vehicle.registrationPlate}</span>
            </div>
          )}
          {vehicle.firstRegistrationDate && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Prva registracija</span>
              <span className="font-medium">{transformDateToLocalDateString(vehicle.firstRegistrationDate)}</span>
            </div>
          )}
          {vehicle.colorName && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Boja</span>
              <span className="font-medium">{vehicle.colorName}</span>
            </div>
          )}
          {vehicle.fuelType && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Gorivo</span>
              <span className="font-medium">{vehicle.fuelType}</span>
            </div>
          )}
          {vehicle.kw > 0 && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Snaga</span>
              <span className="font-medium">{vehicle.kw} kW / {vehicle.hp} KS</span>
            </div>
          )}
          {vehicle.kmTraveled > 0 && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Kilometraža</span>
              <span className="font-medium">{vehicle.kmTraveled.toLocaleString()} km</span>
            </div>
          )}
          {vehicle.cylinderCapacity && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Zapremina</span>
              <span className="font-medium">{vehicle.cylinderCapacity} ccm</span>
            </div>
          )}
          {((vehicle.price?.sellingPrice ?? 0) > 0 || (vehicle.price?.netPrice ?? 0) > 0) && (
            <>
              <div className="border-t border-slate-600 my-2"></div>
              {(vehicle.price?.netPrice ?? 0) > 0 && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Neto cijena</span>
                  <span className="font-medium">{formatCurrency.format(vehicle.price!.netPrice!)}</span>
                </div>
              )}
              {(vehicle.price?.sellingPrice ?? 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-300">Prodajna cijena</span>
                  <span className="font-bold text-green-400">{formatCurrency.format(vehicle.price!.sellingPrice!)}</span>
                </div>
              )}
            </>
          )}
        </div>
        </Tooltip>
        <div className="flex gap-1 mt-0.5 text-xs text-slate-500">
          {vehicle.registrationPlate && (
            <span className="bg-slate-50 rounded-sm px-1.5 py-0.5">
              {vehicle.registrationPlate}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(VehicleCell);
